import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, Plug, CheckCircle2, RefreshCw, Link2Off, Users } from "lucide-react";
import { toast } from "sonner";
import { MetaAdsCard } from "@/components/booking/MetaAdsCard";

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // Klaviyo
  const [klStatus, setKlStatus] = useState(null);
  const [klKey, setKlKey] = useState("");
  const [klConnecting, setKlConnecting] = useState(false);
  const [klLists, setKlLists] = useState([]);
  const [klSyncing, setKlSyncing] = useState(false);

  const loadStatus = () => api.get("/mailchimp/status").then(({ data }) => {
    setStatus(data);
    if (data.connected) loadAudiences();
  }).catch(() => {});
  const loadAudiences = () => api.get("/mailchimp/audiences").then(({ data }) => setAudiences(data)).catch(() => {});
  const loadKl = () => api.get("/klaviyo/status").then(({ data }) => {
    setKlStatus(data);
    if (data.connected) api.get("/klaviyo/lists").then(({ data }) => setKlLists(data)).catch(() => {});
  }).catch(() => {});
  useEffect(() => { loadStatus(); loadKl(); }, []);

  const klConnect = async () => {
    setKlConnecting(true);
    try { await api.post("/klaviyo/connect", { api_key: klKey.trim() }); setKlKey(""); toast.success("Klaviyo connected"); loadKl(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't connect"); }
    finally { setKlConnecting(false); }
  };
  const klDisconnect = async () => {
    if (!window.confirm("Disconnect Klaviyo?")) return;
    try { await api.post("/klaviyo/disconnect"); setKlLists([]); toast.success("Disconnected"); loadKl(); }
    catch { toast.error("Couldn't disconnect"); }
  };
  const klPickList = async (id) => {
    const l = klLists.find((x) => x.id === id);
    try { await api.put("/klaviyo/list", { audience_id: id, audience_name: l?.name || "" }); toast.success("List saved"); loadKl(); }
    catch { toast.error("Couldn't save list"); }
  };
  const klSyncAll = async () => {
    setKlSyncing(true);
    try { const { data } = await api.post("/klaviyo/sync-all"); toast.success(`Synced ${data.synced} of ${data.total}${data.failed ? ` (${data.failed} failed)` : ""}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    finally { setKlSyncing(false); }
  };

  const klSyncSuppressions = async () => {
    try { const { data } = await api.post("/klaviyo/sync-suppressions"); toast.success(`${data.switched_off} client(s) unsubscribed from Klaviyo`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      await api.post("/mailchimp/connect", { api_key: apiKey.trim() });
      setApiKey("");
      toast.success("Mailchimp connected");
      loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't connect"); }
    finally { setConnecting(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Mailchimp? Client syncing will stop.")) return;
    try { await api.post("/mailchimp/disconnect"); setAudiences([]); toast.success("Disconnected"); loadStatus(); }
    catch { toast.error("Couldn't disconnect"); }
  };
  const pickAudience = async (id) => {
    const a = audiences.find((x) => x.id === id);
    try { await api.put("/mailchimp/audience", { audience_id: id, audience_name: a?.name || "" }); toast.success("Audience saved"); loadStatus(); }
    catch { toast.error("Couldn't save audience"); }
  };
  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/mailchimp/sync-all");
      toast.success(`Synced ${data.synced} of ${data.total} opted-in clients${data.failed ? ` (${data.failed} failed)` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-8" data-testid="integrations-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><Plug className="h-7 w-7 text-primary" />Integrations</h1>
        <p className="text-muted-foreground mt-1">Connect Azordi to the tools you already use.</p>
      </div>

      <Card className="rounded-2xl border p-6 shadow-sm" data-testid="mailchimp-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#FFE01B] flex items-center justify-center"><Mail className="h-6 w-6 text-neutral-900" /></div>
            <div>
              <div className="font-heading font-bold text-lg">Mailchimp</div>
              <div className="text-sm text-muted-foreground">Sync clients who opt in to marketing into a Mailchimp audience.</div>
            </div>
          </div>
          {status?.connected && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1 shrink-0" data-testid="mc-connected-badge"><CheckCircle2 className="h-3 w-3" />Connected</span>}
        </div>

        {!status?.connected ? (
          <div className="mt-5">
            <label className="text-xs text-muted-foreground">Mailchimp API key</label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="e.g. 0123…cdef-us21" className="h-11 rounded-lg mt-1" data-testid="mc-api-key" />
            <p className="text-xs text-muted-foreground mt-1.5">Find it in Mailchimp → Profile → Extras → API keys. Stored securely; never shown again.</p>
            <Button className="rounded-lg mt-3" onClick={connect} disabled={connecting || !apiKey.trim()} data-testid="mc-connect"><Plug className="h-4 w-4 mr-1.5" />{connecting ? "Connecting…" : "Connect Mailchimp"}</Button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="text-sm">Connected key <span className="font-mono">{status.api_key_masked}</span></div>
            <div>
              <label className="text-xs text-muted-foreground">Audience to sync into</label>
              <Select value={status.audience_id || ""} onValueChange={pickAudience}>
                <SelectTrigger className="h-11 rounded-lg mt-1" data-testid="mc-audience"><SelectValue placeholder="Choose an audience" /></SelectTrigger>
                <SelectContent>{audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.member_count})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" className="rounded-lg" onClick={syncAll} disabled={syncing || !status.audience_id} data-testid="mc-sync-all"><Users className="h-4 w-4 mr-1.5" />{syncing ? "Syncing…" : "Sync all opted-in clients"}</Button>
              <Button variant="ghost" className="rounded-lg text-destructive hover:text-destructive" onClick={disconnect} data-testid="mc-disconnect"><Link2Off className="h-4 w-4 mr-1.5" />Disconnect</Button>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" />New & updated clients who tick "marketing opt-in" sync automatically. Only clients with an email are sent.</p>
            {status.webhook_path && (
              <div className="rounded-lg bg-muted/50 p-2.5 text-xs" data-testid="mc-webhook">
                <div className="font-medium mb-1">Two-way sync — paste this Webhook URL in Mailchimp (Audience → Settings → Webhooks):</div>
                <code className="block break-all text-[11px] bg-background rounded px-2 py-1 border">{window.location.origin}{status.webhook_path}</code>
                <div className="text-muted-foreground mt-1">When a client unsubscribes in Mailchimp, Azordi switches off their marketing opt-in automatically.</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border p-6 shadow-sm mt-5" data-testid="klaviyo-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-neutral-900 flex items-center justify-center"><Mail className="h-6 w-6 text-white" /></div>
            <div>
              <div className="font-heading font-bold text-lg">Klaviyo</div>
              <div className="text-sm text-muted-foreground">Sync opted-in clients into a Klaviyo list.</div>
            </div>
          </div>
          {klStatus?.connected && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1 shrink-0" data-testid="kl-connected-badge"><CheckCircle2 className="h-3 w-3" />Connected</span>}
        </div>

        {!klStatus?.connected ? (
          <div className="mt-5">
            <label className="text-xs text-muted-foreground">Klaviyo private API key</label>
            <Input type="password" value={klKey} onChange={(e) => setKlKey(e.target.value)} placeholder="pk_…" className="h-11 rounded-lg mt-1" data-testid="kl-api-key" />
            <p className="text-xs text-muted-foreground mt-1.5">Klaviyo → Settings → API keys → Create Private API Key (scopes: accounts, lists, profiles). Stored securely.</p>
            <Button className="rounded-lg mt-3" onClick={klConnect} disabled={klConnecting || !klKey.trim()} data-testid="kl-connect"><Plug className="h-4 w-4 mr-1.5" />{klConnecting ? "Connecting…" : "Connect Klaviyo"}</Button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="text-sm">Connected key <span className="font-mono">{klStatus.api_key_masked}</span></div>
            <div>
              <label className="text-xs text-muted-foreground">List to sync into</label>
              <Select value={klStatus.list_id || ""} onValueChange={klPickList}>
                <SelectTrigger className="h-11 rounded-lg mt-1" data-testid="kl-list"><SelectValue placeholder="Choose a list" /></SelectTrigger>
                <SelectContent>{klLists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" className="rounded-lg" onClick={klSyncAll} disabled={klSyncing || !klStatus.list_id} data-testid="kl-sync-all"><Users className="h-4 w-4 mr-1.5" />{klSyncing ? "Syncing…" : "Sync all opted-in clients"}</Button>
              <Button variant="outline" className="rounded-lg" onClick={klSyncSuppressions} data-testid="kl-sync-suppressions"><RefreshCw className="h-4 w-4 mr-1.5" />Sync unsubscribes</Button>
              <Button variant="ghost" className="rounded-lg text-destructive hover:text-destructive" onClick={klDisconnect} data-testid="kl-disconnect"><Link2Off className="h-4 w-4 mr-1.5" />Disconnect</Button>
            </div>
          </div>
        )}
      </Card>
      <MetaAdsCard />
    </div>
  );
}
